/**
 * POPSTAR Home — the network's front door, rebuilt as a MODULAR BENTO DASHBOARD
 * (owner spec, 2026-07-07): a warm greeting over three lively blocks instead of a
 * single stacked feed —
 *   • NEWS       — the top headlines, compact; "All news" opens the full feed
 *                  (the old NewsScreen renders as a sub-view, unchanged);
 *   • PULSE      — the live network heartbeat (players online, recent average,
 *                  network best, total runs). The WHOLE block is a button that
 *                  deep-links to Stats → Network (it exists to be clicked);
 *   • TODAY      — the daily-return driver: the free Wonder Wheel's readiness
 *                  (courtesy state from the SAME localStorage key WheelScreen
 *                  maintains — the server stays the real gate) and, when a
 *                  seasonal event is claimable, the event on top. Both rows
 *                  deep-link into Stickers (Wheel / Seasonal).
 *
 * Presentational + human-timescale: one-shot loads through the {@link GameServices}
 * facade on mount, no per-frame state, no Firebase imports. Navigation is handed UP
 * via callbacks — the shell owns the tabs.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { GameServices } from '../services/types';
import type { EventDoc, NetworkStats, NewsItem, UserProfile } from '../services/dataModel';
import { NewsScreen } from './NewsScreen';
import { useT } from './i18n';

interface HomeDashboardProps {
  services: GameServices;
  /** Live "players online" count (the shell already subscribes to presence). */
  onlineCount: number;
  /** Deep-link: Stats → Network (the pulse block's destination). */
  onGoStats: () => void;
  /** Deep-link: Stickers → Rewards (the merged Prize Wheel + events pane). */
  onGoRewards: () => void;
}

/** Mirrors WheelScreen's courtesy free-spin state (the server is the real gate). */
const FREE_SPIN_KEY = 'barrage.wheel.lastFreeSpin';
const DAY_MS = 24 * 60 * 60 * 1000;

/** Best-effort read of the last free spin; 0 (⇒ ready) when unknown. */
function lastFreeSpinMs(): number {
  try {
    const raw = window.localStorage.getItem(FREE_SPIN_KEY);
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

/** Locale short date from epoch ms (news card stamps). */
function formatDate(epochMs: number): string {
  if (!Number.isFinite(epochMs) || epochMs <= 0) return '';
  return new Date(epochMs).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function HomeDashboard({
  services,
  onlineCount,
  onGoStats,
  onGoRewards,
}: HomeDashboardProps) {
  const t = useT();
  const [view, setView] = useState<'dash' | 'news'>('dash');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [news, setNews] = useState<NewsItem[]>([]);
  const [net, setNet] = useState<NetworkStats | null>(null);
  const [event, setEvent] = useState<EventDoc | null>(null);
  const [loading, setLoading] = useState(true);

  // One-shot dashboard load; each read fails soft so one hiccup can't blank Home.
  const load = useCallback(async () => {
    setLoading(true);
    const [p, feed, stats, ev] = await Promise.all([
      services.getProfile().catch(() => null),
      services.getNews().catch(() => [] as NewsItem[]),
      services.getNetworkStats().catch(() => null),
      services.getActiveEvent().catch(() => null),
    ]);
    setProfile(p);
    setNews([...feed].sort((a, b) => a.order - b.order));
    setNet(stats);
    setEvent(ev);
    setLoading(false);
  }, [services]);

  useEffect(() => {
    void load();
  }, [load]);

  // Free-spin readiness: computed once per mount (human timescale is plenty).
  const spinReady = useMemo(() => Date.now() - lastFreeSpinMs() >= DAY_MS, []);

  const firstName = profile?.name?.trim() ?? '';
  const headlines = news.slice(0, 3);

  // ---- "All news" sub-view: the full classic feed with a way back. ----------
  if (view === 'news') {
    return (
      <div className="home-dash">
        <style>{HOME_CSS}</style>
        <button className="btn btn-ghost home-back" type="button" onClick={() => setView('dash')}>
          ‹ {t('common.back')}
        </button>
        <NewsScreen items={news} loading={loading} onBack={() => setView('dash')} embedded />
      </div>
    );
  }

  return (
    <div className="home-dash">
      <style>{HOME_CSS}</style>

      {/* Greeting — the welcome mat. */}
      <header className="home-hello">
        <h2 className="home-hello-title">
          {t('home.welcome')}
          {firstName ? `, ${firstName}` : ''}{' '}
          <span className="home-wave" aria-hidden>
            👋
          </span>
        </h2>
      </header>

      <div className="home-grid">
        {/* ---- NEWS ---- */}
        <section className="panel home-block home-news">
          <h3 className="home-block-head">
            <span className="home-block-icon" aria-hidden>
              📰
            </span>
            {t('home.newsTitle')}
          </h3>

          {loading ? (
            <div className="home-news-skeleton" aria-hidden>
              <span />
              <span />
              <span />
            </div>
          ) : headlines.length === 0 ? (
            <p className="home-empty muted-line">{t('home.noNews')}</p>
          ) : (
            <ul className="home-news-list">
              {headlines.map((item) => (
                <li key={item.id} className="home-news-item">
                  {item.publishedAt > 0 && (
                    <time className="home-news-date" dateTime={new Date(item.publishedAt).toISOString()}>
                      {formatDate(item.publishedAt)}
                    </time>
                  )}
                  <span className="home-news-title">{item.title}</span>
                  <span className="home-news-body">{item.body}</span>
                </li>
              ))}
            </ul>
          )}

          {news.length > headlines.length && (
            <button className="home-link" type="button" onClick={() => setView('news')}>
              {t('home.viewAll')} <span aria-hidden>›</span>
            </button>
          )}
        </section>

        {/* ---- NETWORK PULSE (one big button → Stats → Network) ---- */}
        <button className="panel home-block home-pulse" type="button" onClick={onGoStats}>
          <h3 className="home-block-head">
            <span className="home-block-icon home-live-dot" aria-hidden />
            {t('home.pulseTitle')}
          </h3>
          <div className="home-pulse-grid">
            <span className="home-pulse-cell">
              <b>{onlineCount.toLocaleString()}</b>
              <i>{t('net.playersOnline')}</i>
            </span>
            <span className="home-pulse-cell">
              <b>{net ? net.recentAvgScore.toLocaleString() : '—'}</b>
              <i>{t('net.recentAverage')}</i>
            </span>
            <span className="home-pulse-cell">
              <b>{net ? net.bestScore.toLocaleString() : '—'}</b>
              <i>{t('net.networkBest')}</i>
            </span>
            <span className="home-pulse-cell">
              <b>{net ? net.totalRuns.toLocaleString() : '—'}</b>
              <i>{t('net.totalRuns')}</i>
            </span>
          </div>
          <span className="home-link">
            {t('home.pulseCta')} <span aria-hidden>›</span>
          </span>
        </button>

        {/* ---- TODAY (free spin + seasonal event) ---- */}
        <section className="panel home-block home-today">
          <h3 className="home-block-head">
            <span className="home-block-icon" aria-hidden>
              ✨
            </span>
            {t('home.todayTitle')}
          </h3>

          {event && (
            <button className="home-today-row home-event" type="button" onClick={onGoRewards}>
              <span className="home-today-icon" aria-hidden>
                🎉
              </span>
              <span className="home-today-text">
                <b>{t('home.eventLive')}</b>
                <i>{t('home.eventCta')}</i>
              </span>
              <span className="home-today-go" aria-hidden>
                ›
              </span>
            </button>
          )}

          <button
            className={'home-today-row home-wheel' + (spinReady ? ' is-ready' : '')}
            type="button"
            onClick={onGoRewards}
          >
            <span className="home-today-icon" aria-hidden>
              🎡
            </span>
            <span className="home-today-text">
              <b>{spinReady ? t('home.spinReady') : t('home.spinWait')}</b>
              <i>{t('home.spinCta')}</i>
            </span>
            <span className="home-today-go" aria-hidden>
              ›
            </span>
          </button>
        </section>
      </div>
    </div>
  );
}

/**
 * Scoped styles for the bento dashboard (nested under `.home-dash`). Shared
 * primitives (`.panel`, `.btn`, `.muted-line`, `pulse-dot`, CSS vars) come from
 * src/index.css; only the grid + block dressing lives here.
 */
const HOME_CSS = `
.home-dash {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  width: 100%;
}
.home-dash .home-back { align-self: flex-start; margin-left: max(0px, calc(50% - 380px)); }

/* ---- Greeting ---- */
.home-dash .home-hello {
  width: min(760px, 96vw);
  text-align: left;
}
.home-dash .home-hello-title {
  margin: 4px 0 2px;
  font-size: 26px;
  font-weight: 900;
  letter-spacing: 0.01em;
  background: linear-gradient(96deg, var(--text), var(--accent-soft));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.home-dash .home-wave {
  display: inline-block;
  animation: home-wave 2.2s var(--ease) infinite;
  transform-origin: 70% 70%;
}
@keyframes home-wave {
  0%, 60%, 100% { transform: rotate(0deg); }
  65%, 75% { transform: rotate(16deg); }
  70%, 80% { transform: rotate(-8deg); }
}
/* ---- The bento grid ---- */
.home-dash .home-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr);
  grid-template-areas:
    'news pulse'
    'news today';
  gap: 14px;
  width: min(760px, 96vw);
  align-items: stretch;
}
.home-dash .home-news { grid-area: news; }
.home-dash .home-pulse { grid-area: pulse; }
.home-dash .home-today { grid-area: today; }

.home-dash .home-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: auto;
  margin: 0;
  text-align: left;
  animation: home-in 0.45s var(--ease-out) both;
}
.home-dash .home-block:nth-child(2) { animation-delay: 0.07s; }
.home-dash .home-block:nth-child(3) { animation-delay: 0.14s; }
@keyframes home-in {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .home-dash .home-block, .home-dash .home-wave { animation: none; }
}

.home-dash .home-block-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 12px;
  font-weight: 800;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.12em;
}
.home-dash .home-block-icon { font-size: 14px; }
.home-dash .home-live-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--success);
  animation: pulse-dot 2s var(--ease) infinite;
}

.home-dash .home-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  align-self: flex-start;
  margin-top: auto;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  color: var(--accent-soft);
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
}
.home-dash .home-link:hover { color: var(--text); }

/* ---- News block ---- */
.home-dash .home-news-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.home-dash .home-news-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--glass-border);
}
.home-dash .home-news-item:last-child { border-bottom: 0; padding-bottom: 0; }
.home-dash .home-news-date {
  font-size: 10px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.home-dash .home-news-title {
  font-size: 15px;
  font-weight: 800;
  color: var(--text);
  line-height: 1.25;
}
.home-dash .home-news-body {
  font-size: 12.5px;
  color: var(--text-dim);
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.home-dash .home-empty { margin: 0; font-size: 13px; }
.home-dash .home-news-skeleton {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.home-dash .home-news-skeleton span {
  height: 40px;
  border-radius: var(--radius-sm);
  background: var(--panel-2);
  overflow: hidden;
  position: relative;
}
.home-dash .home-news-skeleton span::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, var(--glass-hi), transparent);
  animation: news-shimmer 1.4s infinite;
}

/* ---- Pulse block (a big friendly button) ---- */
.home-dash .home-pulse {
  cursor: pointer;
  font: inherit;
  color: var(--text);
  transition: transform var(--t-fast) var(--ease), border-color var(--t-fast) var(--ease),
    box-shadow var(--t-fast) var(--ease);
}
.home-dash .home-pulse:hover {
  transform: translateY(-2px);
  border-color: var(--accent);
  box-shadow: var(--shadow-2), 0 0 24px var(--accent-glow);
}
.home-dash .home-pulse-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.home-dash .home-pulse-cell {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 8px 10px;
  background: var(--panel-2);
  border-radius: var(--radius-sm);
}
.home-dash .home-pulse-cell b {
  font-size: 19px;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
  color: var(--text);
  line-height: 1.1;
}
.home-dash .home-pulse-cell i {
  font-style: normal;
  font-size: 10px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* ---- Today block ---- */
.home-dash .home-today-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font: inherit;
  text-align: left;
  color: var(--text);
  background: var(--panel-2);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius);
  padding: 10px 12px;
  cursor: pointer;
  transition: transform var(--t-fast) var(--ease), border-color var(--t-fast) var(--ease),
    box-shadow var(--t-fast) var(--ease);
}
.home-dash .home-today-row:hover { transform: translateY(-1px); border-color: var(--glass-border-strong); }
.home-dash .home-today-row.is-ready {
  border-color: rgba(255, 209, 102, 0.5);
  box-shadow: 0 0 18px rgba(255, 209, 102, 0.18);
}
.home-dash .home-event {
  border-color: var(--accent);
  box-shadow: 0 0 18px var(--accent-glow);
}
.home-dash .home-today-icon { font-size: 24px; flex: 0 0 auto; }
.home-dash .home-today-row.is-ready .home-today-icon {
  animation: home-wheel-rock 2.6s var(--ease) infinite;
}
@keyframes home-wheel-rock {
  0%, 70%, 100% { transform: rotate(0deg); }
  78% { transform: rotate(-12deg); }
  88% { transform: rotate(10deg); }
}
@media (prefers-reduced-motion: reduce) {
  .home-dash .home-today-row.is-ready .home-today-icon { animation: none; }
}
.home-dash .home-today-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1;
}
.home-dash .home-today-text b { font-size: 14px; font-weight: 800; color: var(--text); }
.home-dash .home-today-text i {
  font-style: normal;
  font-size: 11.5px;
  color: var(--muted);
}
.home-dash .home-today-go { font-size: 20px; font-weight: 800; color: var(--muted); }

/* ---- Narrow screens: single column, actionable blocks first-ish ---- */
@media (max-width: 640px) {
  .home-dash .home-grid {
    grid-template-columns: 1fr;
    grid-template-areas:
      'news'
      'pulse'
      'today';
  }
}
`;
